import i18next from 'i18next'
import { Try, toFailure, toSuccess } from '../types/commonTypes'
import { createAxios } from './axios'
import {
  AddMembersParams,
  DeleteMemberParams,
  GetBusinessSettingsParams,
  GetBusinessSettingsResponse,
  UpdateAllMembersSaigeParams,
  UpdateMemberRoleParams,
} from './businessSettingsTypes'
import { TEST_USER_AUTHORIZATION } from './userApiService'
import { Player } from '../pages/settings/organization/components/AddPlayersModal'

const ADD_MEMBERS_MESSAGE = 'USE_BACKEND_DEFAULT'

export async function getBusinessSettingsData({
  authorization = TEST_USER_AUTHORIZATION,
  businessId,
}: GetBusinessSettingsParams): Promise<Try<GetBusinessSettingsResponse>> {
  try {
    const url = `/admin/businesses/${businessId}.json?ux3=true`
    const { data } = await createAxios(authorization).get<GetBusinessSettingsResponse>(url)
    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function addMembersApi({
  authorization = TEST_USER_AUTHORIZATION,
  businessId,
  members,
  message1 = ADD_MEMBERS_MESSAGE,
  message2,
  language,
}: AddMembersParams): Promise<Try<undefined>> {
  try {
    const url = `/admin/businesses/${businessId}/member`
    await createAxios(authorization).post<undefined>(url, {
      business_id: businessId.toString(),
      members,
      message1,
      message2,
      language,
    })
    return toSuccess(undefined)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function changeMemberAdminRoleApi({
  authorization = TEST_USER_AUTHORIZATION,
  businessId,
  userName,
}: UpdateMemberRoleParams): Promise<Try<undefined>> {
  try {
    const url = `/admin/businesses/${businessId}/member_toggle_admin`
    await createAxios(authorization).post<undefined>(url, {
      username: userName,
    })
    return toSuccess(undefined)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function changeMemberSaigeAccessApi({
  authorization = TEST_USER_AUTHORIZATION,
  businessId,
  userName,
}: UpdateMemberRoleParams): Promise<Try<undefined>> {
  try {
    const url = `/admin/businesses/${businessId}/member_toggle_saige_access`
    await createAxios(authorization).post<undefined>(url, {
      username: userName,
    })
    return toSuccess(undefined)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function changeAllMemberSaigeAccessApi({
  authorization = TEST_USER_AUTHORIZATION,
  businessId,
  enabledState,
}: UpdateAllMembersSaigeParams): Promise<Try<undefined>> {
  try {
    const url = `/admin/businesses/${businessId}/member_toggle_saige_access_all`
    await createAxios(authorization).post<undefined>(url, {
      enabled_state: enabledState,
    })
    return toSuccess(undefined)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function removeMemberApi({
  authorization = TEST_USER_AUTHORIZATION,
  businessId,
  userName,
}: DeleteMemberParams): Promise<Try<undefined>> {
  try {
    const url = `/admin/businesses/${businessId}/member_remove`
    await createAxios(authorization).post<undefined>(url, {
      username: userName,
      remove: true,
    })
    return toSuccess(undefined)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function getQuotaUpdate({
  authorization = TEST_USER_AUTHORIZATION,
  businessId,
}: GetBusinessSettingsParams): Promise<Try<GetBusinessSettingsResponse>> {
  try {
    const url = `/admin/businesses/${businessId}/quota_usage.json?ux3=true`
    const { data } = await createAxios(authorization).get<GetBusinessSettingsResponse>(url)
    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function addPlayerAccountCSVApi({
  authorization = TEST_USER_AUTHORIZATION,
  fileName,
  inputFile,
}: {
  authorization?: string | null
  fileName: string
  inputFile: Blob
}): Promise<Try<any>> {
  const formData = new FormData()
  formData.append('name', fileName)
  formData.append('original_filename', fileName)
  formData.append('file', inputFile)
  try {
    const url = '/gamer_accounts/import_gamer_accounts_initial_reading.json'
    const { data } = await createAxios(authorization).post<any>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function addPlayerAccountsCSVApi({
  authorization = TEST_USER_AUTHORIZATION,
  info,
}: {
  authorization?: string | null
  info: any
}): Promise<Try<any>> {
  try {
    const url = '/gamer_accounts/import_gamer_accounts_save.json'
    const { data } = await createAxios(authorization).post<any>(url, {
      already_existed_gamer_accounts: info?.already_existed_gamer_accounts || null,
      newly_created_classes: info?.newly_created_classes || null,
      newly_created_gamer_accounts: info?.newly_created_gamer_accounts || null,
      import_gr_tags: info?.import_gr_tags || null,
      language_field: i18next.language,
    })
    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function addPlayerAccountsApi({
  authorization = TEST_USER_AUTHORIZATION,
  player,
  invitedBy,
  language,
}: {
  authorization?: string | null
  player: Player
  invitedBy: number
  language?: string
}): Promise<Try<any>> {
  try {
    const url = '/gamer_accounts/reg_gamer_account.json'
    if (player.tag && player.tag > 0) {
      const { data } = await createAxios(authorization).post<any>(url, {
        added_class_ids: [player.tag],
        invited_by: invitedBy,
        language_field: language,
        gamer_username: player.email,
        gamer_realname: player.name,
      })
      return toSuccess(data)
    } else {
      const { data } = await createAxios(authorization).post<any>(url, {
        invited_by: invitedBy,
        language_field: language,
        gamer_username: player.email,
        gamer_realname: player.name,
      })
      return toSuccess(data)
    }
  } catch (e: any) {
    return toFailure(e)
  }
}

export async function resendPlayerAccountInviteApi({
  authorization = TEST_USER_AUTHORIZATION,
  playerEmail,
}: {
  authorization?: string | null
  playerEmail: string
}): Promise<Try<any>> {
  try {
    const url = '/gamer_accounts/reg_gamer_account'
    const { data } = await createAxios(authorization).post<any>(url, {
      login_type: 'json_reply',
      gamer_username: playerEmail,
      resend_invitation: true,
    })
    return toSuccess(data)
  } catch (e: any) {
    return toFailure(e)
  }
}
